'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

const steps = {
    dev: [
        { number: '01', title: 'Discovery', subtitle: 'Understanding Goals', desc: 'In-depth consultation to map out business goals, target audience, and technical feasibility.', icon: 'MagnifyingGlassIcon' },
        { number: '02', title: 'Strategy', subtitle: 'Blueprint & Planning', desc: 'Comprehensive roadmap comprising timelines, wireframes, user flows, and architecture.', icon: 'MapIcon' },
        { number: '03', title: 'Design', subtitle: 'Visual Excellence', desc: 'Crafting pixel-perfect, intuitive interfaces that align perfectly with your brand identity.', icon: 'PaintBrushIcon' },
        { number: '04', title: 'Development', subtitle: 'Robust Engineering', desc: 'Turning designs into high-performance code using modern frameworks and best practices.', icon: 'CommandLineIcon' },
        { number: '05', title: 'Testing', subtitle: 'Quality Assurance', desc: 'Rigorous testing across all devices and scenarios to ensure a flawless user experience.', icon: 'ShieldCheckIcon' },
        { number: '06', title: 'Launch', subtitle: 'Go Live', desc: 'Seamless deployment with zero downtime, including SSL setup and team training.', icon: 'RocketLaunchIcon' }
    ],
    seo: [
        { number: '01', title: 'Audit', subtitle: 'Digital Health Check', desc: 'Complete technical SEO audit, backlink analysis, and competitor benchmarking.', icon: 'ChartBarIcon' },
        { number: '02', title: 'Keywords', subtitle: 'Search Strategy', desc: 'Identifying high-intent keywords and content gaps to drive qualified organic traffic.', icon: 'MagnifyingGlassCircleIcon' },
        { number: '03', title: 'Optimization', subtitle: 'On-Page SEO', desc: 'Fine-tuning meta tags, headers, and structure for maximum search engine visibility.', icon: 'AdjustmentsHorizontalIcon' },
        { number: '04', title: 'Local SEO', subtitle: 'GMB Dominance', desc: 'Optimizing Google Business Profile availability to capture local market share.', icon: 'MapPinIcon' },
        { number: '05', title: 'Content', subtitle: 'Authority Building', desc: 'Creating high-value content that establishes your brand as an industry thought leader.', icon: 'DocumentTextIcon' },
        { number: '06', title: 'Growth', subtitle: 'Analysis & Scale', desc: 'Monthly performance reporting with actionable insights for continuous improvement.', icon: 'PresentationChartLineIcon' }
    ]
};

const MobileUnifiedProcess = () => {
    const [activeTab, setActiveTab] = useState<'dev' | 'seo'>('dev');
    const [expandedIndex, setExpandedIndex] = useState<number | null>(0); // Default open first one

    const toggleStep = (index: number) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    return (
        <section className="py-20 px-4 pb-32 bg-slate-50 min-h-screen">
            {/* Header */}
            <div className="mb-10 text-center">
                <h2 className="text-4xl font-black text-slate-900 tracking-tight leading-[1.1] mb-2">
                    Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Process</span>
                </h2>
                <p className="text-sm text-slate-500 font-medium">
                    Streamlined workflow for maximum impact.
                </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex justify-center mb-10">
                <div className="inline-flex p-1 rounded-2xl bg-white border border-slate-200 shadow-sm relative">
                    {/* Sliding Background */}
                    <motion.div
                        className="absolute top-1 bottom-1 w-[130px] rounded-xl bg-slate-900 shadow-md z-0"
                        initial={false}
                        animate={{ x: activeTab === 'dev' ? 0 : 130 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />

                    <button
                        onClick={() => { setActiveTab('dev'); setExpandedIndex(0); }}
                        className={`relative z-10 w-[130px] py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${activeTab === 'dev' ? 'text-white' : 'text-slate-500'}`}
                    >
                        Development
                    </button>
                    <button
                        onClick={() => { setActiveTab('seo'); setExpandedIndex(0); }}
                        className={`relative z-10 w-[130px] py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${activeTab === 'seo' ? 'text-white' : 'text-slate-500'}`}
                    >
                        SEO & Brand
                    </button>
                </div>
            </div>

            {/* Accordion Stack */}
            <div className="flex flex-col gap-3">
                <AnimatePresence mode='wait'>
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className="flex flex-col gap-3"
                    >
                        {steps[activeTab].map((step, i) => {
                            const isOpen = expandedIndex === i;
                            return (
                                <motion.div
                                    key={i}
                                    layout
                                    onClick={() => toggleStep(i)}
                                    initial={{ borderRadius: 16 }}
                                    className={`relative border overflow-hidden ${isOpen
                                        ? 'bg-white border-indigo-100 shadow-lg ring-1 ring-indigo-50'
                                        : 'bg-white border-slate-100 shadow-sm active:scale-[0.99]'}
                                        transition-shadow cursor-pointer`}
                                    transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
                                >
                                    {/* Collapsed Header Line */}
                                    <motion.div layout="position" className="flex items-center justify-between p-5">
                                        <div className="flex items-center gap-4">
                                            <span className={`text-xl font-black ${isOpen ? 'text-indigo-600' : 'text-slate-300'}`}>
                                                {step.number}
                                            </span>
                                            <span className={`font-bold text-lg ${isOpen ? 'text-slate-900' : 'text-slate-600'}`}>
                                                {step.title}
                                            </span>
                                        </div>
                                        <motion.div
                                            animate={{ rotate: isOpen ? 180 : 0 }}
                                            transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                            className={`${isOpen ? 'text-indigo-600' : 'text-slate-400'}`}
                                        >
                                            <Icon name="ChevronDownIcon" className="w-5 h-5" />
                                        </motion.div>
                                    </motion.div>

                                    {/* Expanded Content */}
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
                                            >
                                                <div className="px-5 pb-6 pt-0">
                                                    <div className="h-px w-full bg-slate-100 mb-5" />

                                                    <div className="flex items-start gap-4 mb-4">
                                                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                                            <Icon name={step.icon as any} className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-1">
                                                                {step.subtitle}
                                                            </div>
                                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                                {step.desc}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};

export default MobileUnifiedProcess;
